import { useState } from 'react';

/**
 * WHICH COMPANY DO YOU WANT TO BE IN — #247.
 *
 * WHAT IT REPLACES. Nothing asked. Somebody in more than one company who opened
 * the plain address was put into whichever they joined FIRST — a rule inherited
 * from migration 0010 and never revisited, because it was invisible while
 * everybody had one company. Vinnie hit it on 24 August 2026: signed in, saw
 * his own workspace named in the corner, and had a viewer's permissions in a
 * company he administers. `my_tenant()` had quietly put him somewhere else.
 *
 * HIS INSTRUCTION, THE SAME DAY: *"The oldest membership should not win, it
 * should prompt you to and ask which company do you want to be in."*
 *
 * WHY IT MUST COME BEFORE THE BOARD. `my_tenant()` decides the answer to every
 * question the app asks. A board drawn first and corrected afterwards shows one
 * company's work under another company's name, which is the fault #218 exists
 * to end — so this stands in front, and nothing is fetched until it is answered.
 *
 * ONLY WHEN THERE IS A REAL QUESTION. One company, or a company named by the
 * address, and this never appears. Asking somebody something they cannot get
 * wrong is how a screen becomes a keystroke.
 *
 * NOT WHAT THE SIGN-IN TOKEN REMEMBERS, though it was at first, and that is why
 * the first build of this screen could not be seen at all: the token holds
 * whichever company was last picked and is never cleared, so every account that
 * had ever used the switcher was answered from memory. See whichCompany().
 *
 * A SUSPENDED COMPANY IS SHOWN AND CANNOT BE PICKED. `my_tenant()` refuses to
 * put anybody in one, so an entry that looked ordinary and then did nothing
 * would be the same silence in a new place. It says why instead.
 */
export default function WhichCompany({
  mine,
  me,
  onChoose,
}: {
  mine: { id: string; name: string; slug: string; suspended: boolean }[];
  /** So somebody signed in as the wrong account sees that before they choose. */
  me: string;
  onChoose: (w: { id: string; slug: string }) => Promise<void>;
}) {
  const [going, setGoing] = useState('');
  const [problem, setProblem] = useState('');

  const go = async (w: { id: string; slug: string }) => {
    setGoing(w.id);
    setProblem('');
    try {
      await onChoose(w);
    } catch (e) {
      // Said on the screen rather than swallowed — the switcher does the same,
      // for the same reason: a choice that appears to do nothing wastes an hour.
      setProblem((e as Error).message || 'That did not work. Try again.');
      setGoing('');
    }
  };

  return (
    <div className="center-note company-refused">
      <h2 className="verify-title">Which company do you want to open?</h2>
      <p className="company-refused-quiet">
        You are in more than one. This decides whose work you see, and what you are
        allowed to do in it.
      </p>
      <p className="company-refused-who">Signed in as {me}</p>

      <div className="company-mine">
        {mine.map((w) => (
          <button
            key={w.id}
            className="company-row"
            disabled={w.suspended || !!going}
            onClick={() => void go(w)}
          >
            <span className="company-mark">{(w.name[0] ?? '?').toUpperCase()}</span>
            <span className="company-row-name">
              {w.name}
              {w.suspended && <span className="company-row-sub">Suspended — cannot be opened</span>}
            </span>
            <span className="company-row-go">{going === w.id ? 'Opening…' : 'Open'}</span>
          </button>
        ))}
      </div>

      {problem && <div className="login-error">{problem}</div>}

      <p className="company-refused-quiet">
        The address will say which one you are in, so a bookmark from here does not
        ask again. You can change company at any time from the top of the sidebar.
      </p>
    </div>
  );
}
